import { useCallback, useMemo } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer } from 'recharts';

import type { ChartDatapointSelection } from '@/types/reports';

import type { ChartConfiguration, PieChartOptions } from '../types';
import { NON_MEASURE_ROW_KEYS } from '../utils/nativeDataTransforms';
import {
  isDimmed,
  makeSeriesClickHandler,
  markOpacity,
  useSeriesKeyboardSelection,
} from './chartSelection';
import { ChartTooltip, PieLegendContent } from './parts';
import {
  CHART_SERIES_ANIMATION,
  DEFAULT_PIE_OPTIONS,
  resolveCssColor,
} from './shared';

/** Hoisted: avoids creating a new object on every render when interactive. */
const POINTER_CURSOR_STYLE = { cursor: 'pointer' } as const;

/**
 * PieChartCase component - extracted to avoid hooks inside switch statement
 */
interface PieChartCaseProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Chart data structure is dynamic
  chartData: any[];
  config: ChartConfiguration;
  colors: string[];
  height: number;
  dimensionName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- formatXLabel handles dynamic value types
  formatXLabel: (value: any, dimensionName?: string) => string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts Formatter type is complex, using any for callback
  tooltipFormatter: (value: any, name: any, entry: any) => any;
  legendAlign: 'left' | 'center' | 'right';
  /** Formats the slice total for the donut-center label. */
  formatTotal: (value: number) => string;
  /** Called when the user clicks (or keyboard-activates) a pie slice. */
  onDatapointSelect?: (sel: ChartDatapointSelection) => void;
  /** The currently active/selected datapoint; non-matching slices are dimmed. */
  activeSelection?: ChartDatapointSelection | null;
}

// Keys excluded when deriving the single pie measure key from chartData: the
// shared non-measure keys plus `name`, which the native pie path already
// carries as its label.
const PIE_KEY_EXCLUDE = new Set([...NON_MEASURE_ROW_KEYS, 'name']);

export function PieChartCase({
  chartData,
  config,
  colors,
  height,
  dimensionName,
  formatXLabel,
  tooltipFormatter,
  legendAlign,
  formatTotal,
  onDatapointSelect,
  activeSelection,
}: PieChartCaseProps) {
  const pieOptions =
    (config.chartOptions as PieChartOptions) || DEFAULT_PIE_OPTIONS;

  // Derive the single measure key used as seriesKey in ChartDatapointSelection.
  const measureKey =
    chartData.length > 0
      ? (Object.keys(chartData[0]).find(k => !PIE_KEY_EXCLUDE.has(k)) ??
        'value')
      : 'value';

  // Single memoized click handler for all pie cells; Recharts calls it with
  // (entry, index) where entry is the pieData item, which has __sourceRowIndex.
  const clickHandler = useMemo(
    () => makeSeriesClickHandler(measureKey, onDatapointSelect),
    [measureKey, onDatapointSelect]
  );

  const interactive = Boolean(onDatapointSelect);

  const containerStyle = useMemo(
    () => ({ width: '100%', height, minWidth: 300, minHeight: 300 }),
    [height]
  );

  // Transform data to have 'name', 'value', and '__sourceRowIndex' for pie
  // chart compatibility and selection. Native data already has {name, value}
  // shape; Cube data has {x, [measureKey]} shape. formatXLabel renders missing
  // group values — null or '' — as an em-dash.
  const pieData = useMemo(() => {
    const allPieData = chartData.map((item, index) => {
      const rawIdx = (item as Record<string, unknown>)['__sourceRowIndex'];
      const sourceRowIndex = typeof rawIdx === 'number' ? rawIdx : null;

      // Check if data is already in pie format (native path)
      if ('name' in item && 'value' in item) {
        return {
          name: String(
            formatXLabel(item.name, dimensionName) ?? `Item ${index + 1}`
          ),
          value: Number(item.value) || 0,
          __sourceRowIndex: sourceRowIndex,
        };
      }

      // Cube data: has {x, [measureKey]} shape, needs transformation.
      // Exclude the non-measure row keys from the dataKey search.
      const dataKey =
        Object.keys(item).find(key => !NON_MEASURE_ROW_KEYS.has(key)) ||
        'value';
      return {
        name: String(
          formatXLabel(item.x, dimensionName) ?? `Item ${index + 1}`
        ),
        value: Number(item[dataKey as keyof typeof item]) || 0,
        __sourceRowIndex: sourceRowIndex,
      };
    });
    // A zero-value group contributes no arc, but recharts still gives it a
    // padding-angle notch and a legend entry — drop such slices (unless every
    // group is zero, where filtering would blank the chart entirely).
    const nonZeroPieData = allPieData.filter(entry => entry.value !== 0);
    return nonZeroPieData.length > 0 ? nonZeroPieData : allPieData;
  }, [chartData, formatXLabel, dimensionName]);

  // Recharts' Pie hardcodes `tabIndex: -1` on every sector, so a slice can
  // never take focus however we mark up its <Cell>. Keyboard selection is
  // therefore container-level, exactly as for Line/Area: hovering a slice
  // records it, and Enter/Space on the chart dispatches it.
  const seriesKeys = useMemo(() => [measureKey], [measureKey]);
  const { handleMouseMove, handleContainerKeyDown } =
    useSeriesKeyboardSelection({
      chartData: pieData,
      seriesKeys,
      onDatapointSelect,
    });
  const handleSliceMouseEnter = useCallback(
    (entry: unknown) => {
      handleMouseMove({
        activePayload: [{ payload: entry, dataKey: measureKey }],
      });
    },
    [handleMouseMove, measureKey]
  );

  const isDonut = pieOptions.innerRadius > 0;
  // Legend entries mirror the slices explicitly: a pie's legend names its
  // slices, and recharts' derived payload can fall back to the series
  // dataKey — rendering a single literal "value" entry instead.
  const legendPayload = pieData.map((entry, index) => ({
    value: entry.name,
    color: colors[index % colors.length],
    type: 'circle' as const,
  }));
  // Slices are separated by a bg-colored stroke + padding angle; a single
  // slice keeps neither (a padding gap on a full circle reads as a glitch).
  const hasMultipleSlices = pieData.length > 1;
  const sliceStroke = resolveCssColor('--color-bg-primary') || '#FFFFFF';
  const total = pieData.reduce((sum, entry) => sum + entry.value, 0);
  // Scale with the pane but cap the diameter: tall preview panes otherwise
  // blow the donut up to a cartoonish ring. Inner radius is a fraction of
  // the OUTER radius (the hole size users configure) — recharts' own
  // percentage inner radius is pane-relative, which breaks once the outer
  // radius is capped in px.
  const outerRadiusPx = Math.min(Math.round(height * 0.4), 200);
  const innerRadiusPx = Math.round(
    outerRadiusPx * (pieOptions.innerRadius / 100)
  );

  return (
    <div
      style={containerStyle}
      className={interactive ? 'outline-none' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? handleContainerKeyDown : undefined}
      aria-label={
        interactive
          ? 'Interactive pie chart — press Enter or Space to drill into the hovered slice, or the first slice if none has been hovered'
          : undefined
      }
    >
      <ResponsiveContainer
        width='100%'
        height='100%'
        minWidth={300}
        minHeight={300}
      >
        <PieChart style={interactive ? POINTER_CURSOR_STYLE : undefined}>
          <Pie
            data={pieData}
            dataKey='value'
            nameKey='name'
            cx='50%'
            cy='50%'
            innerRadius={innerRadiusPx}
            outerRadius={outerRadiusPx}
            startAngle={pieOptions.startAngle}
            endAngle={pieOptions.startAngle - 360}
            paddingAngle={hasMultipleSlices ? 2 : 0}
            stroke={sliceStroke}
            strokeWidth={hasMultipleSlices ? 2 : 0}
            cornerRadius={isDonut && hasMultipleSlices ? 4 : 0}
            label={pieOptions.labelPosition !== 'none'}
            labelLine={pieOptions.labelPosition === 'outside'}
            {...CHART_SERIES_ANIMATION}
            // On <Pie>, not on each <Cell>: Recharts calls a Cell's onClick
            // with the DOM event, so the handler never saw a datum and every
            // slice click was a no-op. <Pie> is called with the sector, which
            // carries `__sourceRowIndex`.
            onClick={clickHandler}
            onMouseEnter={interactive ? handleSliceMouseEnter : undefined}
          >
            {/* Group-by slice names are unique per category, so the name is
                a stable key. */}
            {pieData.map((entry, index) => {
              // Mirror the bar chart: once any slice is selected, a
              // non-drillable Top-N "Other" slice (no __sourceRowIndex) dims
              // too rather than staying fully opaque.
              let sliceDimmed = false;
              if (activeSelection !== null && activeSelection !== undefined) {
                sliceDimmed =
                  typeof entry.__sourceRowIndex === 'number'
                    ? isDimmed(
                        activeSelection,
                        entry.__sourceRowIndex,
                        measureKey
                      )
                    : true;
              }
              return (
                <Cell
                  key={`slice-${entry.name}`}
                  fill={colors[index % colors.length]}
                  fillOpacity={markOpacity(sliceDimmed)}
                />
              );
            })}
          </Pie>
          {isDonut && (
            <text x='50%' y='50%' textAnchor='middle'>
              <tspan
                x='50%'
                dy='-0.5em'
                style={{ fill: 'var(--color-text-quaternary)', fontSize: 13 }}
              >
                Total
              </tspan>
              <tspan
                x='50%'
                dy='1.35em'
                style={{
                  fill: 'var(--color-text-primary)',
                  fontSize: 24,
                  fontWeight: 600,
                }}
              >
                {formatTotal(total)}
              </tspan>
            </text>
          )}

          <ChartTooltip
            show={config.tooltip.show}
            formatter={tooltipFormatter}
            isPieChart
          />
          {config.legend.show && (
            <Legend
              verticalAlign={
                config.legend.position === 'top' ||
                config.legend.position === 'bottom'
                  ? config.legend.position
                  : 'bottom'
              }
              align={legendAlign}
              layout={config.legend.layout}
              content={
                <PieLegendContent
                  payloadOverride={legendPayload}
                  align={legendAlign}
                  layout={config.legend.layout}
                />
              }
            />
          )}
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
